import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Mail,
  Send,
  TestTube,
  CheckCircle,
  AlertCircle,
  Users,
  Monitor,
  Smartphone,
  Code2,
  Eye,
  RefreshCw,
  Sparkles,
  Save,
  Plus,
  Trash2,
  FileText,
  Download,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Email template. Each feature card pulls a real screenshot from /public via
// the production domain so it works in any inbox. Logo at the top-left,
// elegant editorial dark-mode design set in the native system font stack
// (San Francisco on Apple, Segoe UI on Windows, Roboto on Android).
const DEFAULT_HTML = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta name="color-scheme" content="dark only" />
    <meta name="supported-color-schemes" content="dark only" />
    <title>What's new at Defera</title>
    <style>
      /* No web fonts: email clients overwhelmingly ignore them, and external
         font URLs trip deliverability linters. Everything is set in the
         native system stack — San Francisco on Apple, Segoe UI on Windows,
         Roboto on Android — for a clean, platform-native look. */
      /* Force dark backgrounds in clients that auto-invert. */
      :root { color-scheme: dark only; supported-color-schemes: dark only; }
      body, table, td { background: #000000 !important; }
      /* Disable iOS auto-linking of phone numbers / addresses. */
      a[x-apple-data-detectors] { color: inherit !important; text-decoration: none !important; }
      /* Footer utility links inherit a muted grey instead of body color. */
      a.footer-link { color: #6B7283 !important; text-decoration: underline; }
      /* CTA button uses the Defera brand-600 (#1025B2) royal blue rather
         than pure white, because dark-mode email clients (iOS Mail, Gmail
         mobile) aggressively auto-invert near-white and near-black
         backgrounds — they assume those are "light mode artifacts" and
         flip them to look like dark mode. Saturated mid-spectrum brand
         colors are left alone because the client can tell they're an
         intentional design choice, not an accidental light-mode leftover.
         We still use bgcolor + CSS belt-and-suspenders, plus the inner
         <span> color reinforcement, but the real win is the color itself. */
      .cta-btn-cell {
        background-color: #1025B2 !important;
        border-radius: 999px !important;
      }
      .cta-btn-link,
      .cta-btn-link span {
        color: #FFFFFF !important;
        -webkit-text-fill-color: #FFFFFF !important;
        mso-line-height-rule: exactly;
      }
      @media (prefers-color-scheme: dark) {
        .cta-btn-cell {
          background-color: #1025B2 !important;
        }
        .cta-btn-link,
        .cta-btn-link span {
          color: #FFFFFF !important;
          -webkit-text-fill-color: #FFFFFF !important;
        }
      }
      @media (max-width: 600px) {
        .hero-h1 { font-size: 38px !important; line-height: 1.02 !important; letter-spacing: -0.015em !important; }
        .feature-h2 { font-size: 24px !important; letter-spacing: -0.015em !important; }
        .container { padding: 24px 16px !important; }
        .card-pad { padding: 20px 18px !important; }
      }
    </style>
  </head>
  <body style="margin:0;padding:0;background:#000000;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#E6E8EF;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#000000;">
      <tr>
        <td align="center" class="container" style="padding:40px 20px;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;background:#000000;">

            <!-- Brand header: logo top-left.
                 Uses the original defera-transparent.png — we tried a
                 smaller optimized variant but iOS Mail mobile wouldn't
                 render it for reasons we couldn't pin down, while this
                 source file has rendered reliably across all clients for
                 a long time. Browsers/email clients downscale it from
                 3414x1917 -> 120px display without trouble. -->
            <tr>
              <td style="padding:0 4px 36px 4px;">
                <img src="https://defera.app/defera-transparent.png" alt="Defera" width="120" height="67" decoding="async" style="display:block;width:120px;max-width:120px;height:67px;border:0;outline:none;" />
              </td>
            </tr>

            <!-- Hero -->
            <tr>
              <td style="padding:0 4px 20px 4px;">
                <h1 class="hero-h1" style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:56px;line-height:1.0;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                  What's new at Defera.
                </h1>
              </td>
            </tr>
            <tr>
              <td style="padding:0 4px 44px 4px;">
                <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:17px;line-height:1.6;color:#9BA3B8;font-weight:400;letter-spacing:-0.005em;max-width:540px;">
                  A handful of upgrades that make every trip a little more first class \u2014 from AI-powered price forecasts to lower upfront deposits.
                </p>
              </td>
            </tr>

            <!-- Aspirational opener: Bali lifestyle card (shared with the
                 welcome email). Portrait 800x1200 -> displays 600x900.
                 Loads eagerly since it's the first visual in the email. -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/bali-email.jpg" alt="Cliffside ocean view in Bali" width="600" height="900" loading="eager" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="card-pad" style="padding:28px 28px 30px 28px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">Somewhere worth flying to</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        The world just got closer.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        Bali. Tokyo. Paris. The destinations you have been dreaming of for months are no longer reserved for someday. Every flight on Defera can be secured today with a small deposit \u2014 and paid for comfortably, on your own schedule.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Section divider: separates the aspirational opener above from
                 the product updates below, so readers can tell what is a
                 shipped feature vs. brand storytelling. -->
            <tr>
              <td style="padding:0 4px 36px 4px;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>
                    <td style="border-top:1px solid #1E1E24;padding:28px 0 0 0;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:12px;letter-spacing:0.12em;text-transform:uppercase;color:#FFFFFF;font-weight:700;">The latest from Defera</div>
                      <p style="margin:8px 0 0 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.6;color:#6B7283;font-weight:400;">
                        Five new upgrades, already live in your account.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Feature 1: Seat selection -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <!-- 1200x1644 source -> displayed 600px wide.
                           Height attribute reserves layout space so the email
                           doesn't reflow as the image loads in. The Bali
                           opener above loads eagerly (above the fold); this
                           and everything below are lazy.
                           PNG (palette) chosen over JPG because iOS Mail
                           mobile reliably renders PNGs but breaks on JPGs
                           even with stripped metadata + baseline encoding. -->
                      <img src="https://defera.app/seats-email.png" alt="Live seat map" width="600" height="822" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="card-pad" style="padding:28px 28px 30px 28px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">01 \u2014 Seat selection</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        Pick your seat from a live map.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        No more mystery middle seats. See exactly what's open on your aircraft \u2014 exit rows, extra-legroom, window or aisle \u2014 and lock in your spot before checkout.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Feature 2: Change flight -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/changeflight-email.png" alt="Change your flight" width="600" height="376" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="card-pad" style="padding:28px 28px 30px 28px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">02 \u2014 Change flight, instantly</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        Plans change. Now your flight can too.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        Swap to a different date, time, or route in just a few taps. We'll re-price it on the spot \u2014 you only pay the difference, if any.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Feature 3: Defera Credit on cancel -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <!-- cancelflight kept as PNG: the source is mostly flat
                           color and is already smaller (~109 KB) than any
                           JPEG re-encode we could produce. -->
                      <img src="https://defera.app/cancelflight-cropped.png" alt="Cancel for 100% Defera Credit" width="600" height="550" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="card-pad" style="padding:28px 28px 30px 28px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">03 \u2014 Flexible cancellations</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        Cancel anytime. Get 100% back as credit.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        Life happens. On eligible flights, cancel and we'll restore 100% of what you've paid as Defera Credit \u2014 applied instantly, never expires, and usable on any future trip (even one you book for someone else).
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Feature 4: Price Forecast -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/priceforecast-email.png" alt="AI Price Forecast" width="600" height="456" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="card-pad" style="padding:28px 28px 30px 28px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">04 \u2014 AI Price Forecast</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        Book now, or wait? Now you'll know.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        Our AI studies your exact route and travel dates against months of fare history, then tells you whether prices are likely to rise, hold, or drop \u2014 with a confidence score and price band, right next to your search results.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Feature 5: Lower upfront deposits -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/lowerdeposits-email.png" alt="Lower upfront deposits" width="600" height="428" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="card-pad" style="padding:28px 28px 30px 28px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">05 \u2014 Lower upfront deposits</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        Book sooner. Pay less upfront.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        The further out your trip, the smaller your deposit. Lock in your seat for as little as a few dollars today and spread the rest across weekly or bi-weekly installments \u2014 zero interest, no surprises.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Closing lifestyle section (cards shared with the welcome
                 email). The "Everything you need, in one place" welcome card
                 is intentionally omitted here — its copy duplicates features
                 01/04/05 above. -->

            <!-- Section divider: marks the end of product updates and the
                 start of the editorial/aspirational close. -->
            <tr>
              <td style="padding:0 4px 36px 4px;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>
                    <td style="border-top:1px solid #1E1E24;padding:28px 0 0 0;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:12px;letter-spacing:0.12em;text-transform:uppercase;color:#FFFFFF;font-weight:700;">The Defera way</div>
                      <p style="margin:8px 0 0 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.6;color:#6B7283;font-weight:400;">
                        Travel that fits your life, not the other way around.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Lifestyle: planeseat (landscape 800x600 -> displays 600x450). -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/planeseat-email.jpg" alt="Traveler enjoying a premium cabin with champagne" width="600" height="450" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="card-pad" style="padding:28px 28px 30px 28px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">First class in every sense</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        Fly the way you always imagined.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        Complimentary upgrades on eligible routes. Concierge support when plans shift. Rewards worth using on your next journey. This is what modern travel should feel like \u2014 and what Defera is built to deliver.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Lifestyle: travelstock (landscape 1200x800 -> displays 600x400). -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/travelstock-email.jpg" alt="Couple enjoying a river cruise together" width="600" height="400" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="card-pad" style="padding:28px 28px 30px 28px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">Trips are better together</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        Bring who you love.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        Book for two, four, or the entire party in a single flow. Everyone can split their own installments, or one of you can cover the whole trip \u2014 the choice is yours. What once meant weeks of logistics now begins with a single conversation.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Lifestyle: bathtub (landscape 800x600 -> displays 600x450).
                 Emotional close before the CTA. -->
            <tr>
              <td style="padding:0 0 72px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/bathtub-email.jpg" alt="Unwinding at a hotel on a Defera trip" width="600" height="450" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="card-pad" style="padding:28px 28px 30px 28px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">You have been meaning to do this</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        The moment is yours.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        That vacation will not book itself. But with installments alongside you, it no longer needs to wait for the next paycheck. Your next Defera trip is closer than it has ever been.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Pre-CTA flourish (matches the welcome email close) -->
            <tr>
              <td align="center" style="padding:0 4px 28px 4px;text-align:center;">
                <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:22px;line-height:1.25;color:#FFFFFF;font-weight:600;letter-spacing:-0.015em;text-align:center;">
                  See where life takes you.
                </p>
              </td>
            </tr>

            <!-- CTA: brand-blue pill, bulletproof table-button pattern.
                 bgcolor + CSS background-color + inline color on both <a>
                 and inner <span>. mso-padding-alt keeps Outlook from
                 collapsing the pill. The brand blue is the linchpin that
                 makes this render correctly in iOS Mail dark mode — see
                 the .cta-btn-cell rule above for why. -->
            <tr>
              <td align="center" style="padding:8px 0 16px 0;background:#000000;">
                <table role="presentation" border="0" cellpadding="0" cellspacing="0" align="center" style="margin:0 auto;">
                  <tr>
                    <td align="center" bgcolor="#1025B2" class="cta-btn-cell" style="border-radius:999px;background-color:#1025B2;mso-padding-alt:16px 32px;">
                      <a href="https://defera.app" class="cta-btn-link" style="display:inline-block;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#FFFFFF;-webkit-text-fill-color:#FFFFFF;text-decoration:none;font-weight:600;font-size:15px;line-height:1;padding:16px 32px;border-radius:999px;letter-spacing:-0.005em;mso-line-height-rule:exactly;">
                        <span style="color:#FFFFFF;-webkit-text-fill-color:#FFFFFF;">Open Defera \u2192</span>
                      </a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td align="center" style="padding:0 0 48px 0;">
                <a href="https://defera.app/trips" class="footer-link" style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#6B7283;-webkit-text-fill-color:#6B7283;font-size:13px;text-decoration:underline;font-weight:400;">Or view your trips</a>
              </td>
            </tr>

            <!-- Footer -->
            <tr>
              <td style="padding:0 4px;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>
                    <td style="border-top:1px solid #18181C;padding:24px 0 0 0;">
                      <img src="https://defera.app/defera-transparent.png" alt="Defera" width="72" height="40" decoding="async" style="display:block;width:72px;max-width:72px;height:40px;border:0;outline:none;opacity:0.85;margin-bottom:14px;" />
                      <p style="margin:0 0 8px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:13px;line-height:1.4;color:#9BA3B8;font-weight:600;letter-spacing:-0.01em;">
                        Book now, pay over time.
                      </p>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;line-height:1.7;color:#4A5263;font-weight:400;">
                        You're receiving this because you have a Defera account.<br/>
                        <a href="{{{RESEND_UNSUBSCRIBE_URL}}}" class="footer-link" style="color:#6B7283;-webkit-text-fill-color:#6B7283;text-decoration:underline;">Unsubscribe</a> &nbsp;\u00B7&nbsp;
                        <a href="https://defera.app/privacy" class="footer-link" style="color:#6B7283;-webkit-text-fill-color:#6B7283;text-decoration:underline;">Privacy</a> &nbsp;\u00B7&nbsp;
                        <a href="https://defera.app/contact" class="footer-link" style="color:#6B7283;-webkit-text-fill-color:#6B7283;text-decoration:underline;">Contact</a>
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;

const DEFAULT_SUBJECT = "What's new at Defera \u2014 5 upgrades that make every trip feel first class";
const DEFAULT_PREHEADER = 'AI price forecasts, instant flight changes, lower deposits, and more.';
const DEFAULT_TEMPLATE_NAME = "What's new at Defera";

// Welcome email: sent the first time a customer signs up for a Defera
// account. Same dark theme as the broadcast template, with lifestyle
// photography from the marketing site to set an aspirational tone, and
// the brand-blue CTA pill that survives iOS Mail dark-mode auto-inversion.
const WELCOME_DEFAULT_SUBJECT = 'Welcome to Defera \u2014 your next trip starts here';
const WELCOME_DEFAULT_PREHEADER = 'Book any flight in the world. Pay over time. Zero interest, ever.';
const WELCOME_DEFAULT_HTML = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta name="color-scheme" content="dark only" />
    <meta name="supported-color-schemes" content="dark only" />
    <title>Welcome to Defera</title>
    <style>
      /* No web fonts: email clients overwhelmingly ignore them, and external
         font URLs trip deliverability linters. Everything is set in the
         native system stack — San Francisco on Apple, Segoe UI on Windows,
         Roboto on Android — for a clean, platform-native look. */
      :root { color-scheme: dark only; supported-color-schemes: dark only; }
      body, table, td { background: #000000 !important; }
      a[x-apple-data-detectors] { color: inherit !important; text-decoration: none !important; }
      a.footer-link { color: #6B7283 !important; text-decoration: underline; }
      /* Brand-blue CTA. Saturated mid-spectrum colors survive iOS Mail
         dark-mode auto-inversion (only near-white and near-black get
         flipped). bgcolor + CSS + inner span color is belt-and-suspenders. */
      .cta-btn-cell {
        background-color: #1025B2 !important;
        border-radius: 999px !important;
      }
      .cta-btn-link,
      .cta-btn-link span {
        color: #FFFFFF !important;
        -webkit-text-fill-color: #FFFFFF !important;
        mso-line-height-rule: exactly;
      }
      @media (prefers-color-scheme: dark) {
        .cta-btn-cell { background-color: #1025B2 !important; }
        .cta-btn-link,
        .cta-btn-link span {
          color: #FFFFFF !important;
          -webkit-text-fill-color: #FFFFFF !important;
        }
      }
      @media (max-width: 600px) {
        .hero-h1 { font-size: 42px !important; line-height: 1.02 !important; letter-spacing: -0.015em !important; }
        .feature-h2 { font-size: 24px !important; letter-spacing: -0.015em !important; }
        .container { padding: 24px 16px !important; }
        .card-pad { padding: 20px 18px !important; }
        .lifestyle-pad { padding: 24px 20px 26px 20px !important; }
      }
    </style>
  </head>
  <body style="margin:0;padding:0;background:#000000;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#E6E8EF;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#000000;">
      <tr>
        <td align="center" class="container" style="padding:40px 20px;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;background:#000000;">

            <!-- Brand header -->
            <tr>
              <td style="padding:0 4px 36px 4px;">
                <img src="https://defera.app/defera-transparent.png" alt="Defera" width="120" height="67" decoding="async" style="display:block;width:120px;max-width:120px;height:67px;border:0;outline:none;" />
              </td>
            </tr>

            <!-- Hero -->
            <tr>
              <td style="padding:0 4px 20px 4px;">
                <h1 class="hero-h1" style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:60px;line-height:1.0;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                  Welcome to Defera.
                </h1>
              </td>
            </tr>
            <tr>
              <td style="padding:0 4px 44px 4px;">
                <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:17px;line-height:1.6;color:#9BA3B8;font-weight:400;letter-spacing:-0.005em;max-width:540px;">
                  The trip you have been dreaming about is finally within reach. Book any flight in the world today, and pay for it in comfortable installments over the coming weeks or months. No interest. No credit check. Just the trip you have been waiting for.
                </p>
              </td>
            </tr>

            <!-- Card 1: Bali (portrait 800x1200 -> displays 600x900).
                 Destination aspiration + installments hook. -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/bali-email.jpg" alt="Cliffside ocean view in Bali" width="600" height="900" loading="eager" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="lifestyle-pad" style="padding:32px 32px 34px 32px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">Somewhere worth flying to</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        The world just got closer.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        Bali. Tokyo. Paris. The destinations you have been dreaming of for months are no longer reserved for someday. Every flight on Defera can be secured today with a small deposit \u2014 and paid for comfortably, on your own schedule.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Card 2: seatcloseup (landscape 1200x800 -> displays 600x400).
                 Ease of booking + smart features (live seat map, AI price
                 forecast, installments). -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/seatcloseup-email.jpg" alt="Booking a Defera trip on your phone" width="600" height="400" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="lifestyle-pad" style="padding:32px 32px 34px 32px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">Effortless by design</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        Everything you need, in one place.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        Choose your seat from a live cabin map. Let our AI signal the moments fares are about to rise or ease. Split the total into weekly or bi-weekly installments \u2014 no interest, ever. Considered from search to boarding pass.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Card 3: planeseat (landscape 800x600 -> displays 600x450).
                 Focal point (face + window + champagne) centered vertically.
                 Premium travel experience + upgrades feature. -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/planeseat-email.jpg" alt="Traveler enjoying a premium cabin with champagne" width="600" height="450" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="lifestyle-pad" style="padding:32px 32px 34px 32px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">First class in every sense</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        Fly the way you always imagined.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        Complimentary upgrades on eligible routes. Concierge support when plans shift. Rewards worth using on your next journey. This is what modern travel should feel like \u2014 and what Defera is built to deliver.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Card 4: travelstock (landscape 1200x800 -> displays 600x400).
                 Group / shared trips. -->
            <tr>
              <td style="padding:0 0 56px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/travelstock-email.jpg" alt="Couple enjoying a river cruise together" width="600" height="400" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="lifestyle-pad" style="padding:32px 32px 34px 32px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">Trips are better together</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        Bring who you love.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        Book for two, four, or the entire party in a single flow. Everyone can split their own installments, or one of you can cover the whole trip \u2014 the choice is yours. What once meant weeks of logistics now begins with a single conversation.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Card 5: bathtub (landscape 800x600 -> displays 600x450).
                 Focal point (laughing face) centered vertically.
                 Emotional close ("you deserve this, book it"). -->
            <tr>
              <td style="padding:0 0 72px 0;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#0F0F14;border-radius:18px;box-shadow:0 32px 64px -16px rgba(0,0,0,0.9), 0 12px 24px -8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04);overflow:hidden;">
                  <tr>
                    <td style="padding:0;background:#0F0F14;">
                      <img src="https://defera.app/bathtub-email.jpg" alt="Unwinding at a hotel on your first Defera trip" width="600" height="450" loading="lazy" decoding="async" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;background:#0F0F14;" />
                    </td>
                  </tr>
                  <tr>
                    <td class="lifestyle-pad" style="padding:32px 32px 34px 32px;">
                      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:#6B7283;font-weight:600;margin-bottom:14px;">You have been meaning to do this</div>
                      <h2 class="feature-h2" style="margin:0 0 12px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:30px;line-height:1.05;font-weight:700;color:#FFFFFF;letter-spacing:-0.015em;">
                        The moment is yours.
                      </h2>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#9BA3B8;font-weight:400;">
                        That vacation will not book itself. But with installments alongside you, it no longer needs to wait for the next paycheck. Your first Defera trip is closer than it has ever been.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            <!-- Pre-CTA flourish -->
            <tr>
              <td align="center" style="padding:0 4px 28px 4px;text-align:center;">
                <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:22px;line-height:1.25;color:#FFFFFF;font-weight:600;letter-spacing:-0.015em;text-align:center;">
                  See where life takes you.
                </p>
              </td>
            </tr>

            <!-- CTA: brand-blue pill -->
            <tr>
              <td align="center" style="padding:0 0 16px 0;background:#000000;">
                <table role="presentation" border="0" cellpadding="0" cellspacing="0" align="center" style="margin:0 auto;">
                  <tr>
                    <td align="center" bgcolor="#1025B2" class="cta-btn-cell" style="border-radius:999px;background-color:#1025B2;mso-padding-alt:16px 32px;">
                      <a href="https://defera.app" class="cta-btn-link" style="display:inline-block;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#FFFFFF;-webkit-text-fill-color:#FFFFFF;text-decoration:none;font-weight:600;font-size:15px;line-height:1;padding:16px 32px;border-radius:999px;letter-spacing:-0.005em;mso-line-height-rule:exactly;">
                        <span style="color:#FFFFFF;-webkit-text-fill-color:#FFFFFF;">Plan your first trip \u2192</span>
                      </a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
            <tr>
              <td align="center" style="padding:0 0 48px 0;">
                <a href="https://defera.app/explore" class="footer-link" style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#6B7283;-webkit-text-fill-color:#6B7283;font-size:13px;text-decoration:underline;font-weight:400;">Or browse destinations</a>
              </td>
            </tr>

            <!-- Footer -->
            <tr>
              <td style="padding:0 4px;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>
                    <td style="border-top:1px solid #18181C;padding:24px 0 0 0;">
                      <img src="https://defera.app/defera-transparent.png" alt="Defera" width="72" height="40" decoding="async" style="display:block;width:72px;max-width:72px;height:40px;border:0;outline:none;opacity:0.85;margin-bottom:14px;" />
                      <p style="margin:0 0 8px 0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:13px;line-height:1.4;color:#9BA3B8;font-weight:600;letter-spacing:-0.01em;">
                        Book now, pay over time.
                      </p>
                      <p style="margin:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:11px;line-height:1.7;color:#4A5263;font-weight:400;">
                        You're receiving this because you just created a Defera account.<br/>
                        <a href="https://defera.app/Settings" class="footer-link" style="color:#6B7283;-webkit-text-fill-color:#6B7283;text-decoration:underline;">Email preferences</a> &nbsp;\u00B7&nbsp;
                        <a href="https://defera.app/privacy" class="footer-link" style="color:#6B7283;-webkit-text-fill-color:#6B7283;text-decoration:underline;">Privacy</a> &nbsp;\u00B7&nbsp;
                        <a href="https://defera.app/contact" class="footer-link" style="color:#6B7283;-webkit-text-fill-color:#6B7283;text-decoration:underline;">Contact</a>
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;

export default function Marketing() {
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [preheader, setPreheader] = useState(DEFAULT_PREHEADER);
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [viewport, setViewport] = useState('desktop'); // 'desktop' | 'mobile'
  const [tab, setTab] = useState('preview'); // 'preview' | 'source'

  const [audiences, setAudiences] = useState([]);
  const [audienceId, setAudienceId] = useState('');
  const [defaultFrom, setDefaultFrom] = useState('');
  const [audiencesError, setAudiencesError] = useState(null);
  const [audiencesLoading, setAudiencesLoading] = useState(false);

  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Template library: list of saved {id, name, subject, preheader, html}.
  // `selectedTemplateId` is '' when the editor is showing unsaved/new content.
  // `baseline` is the last loaded/saved snapshot — used to surface the
  // "unsaved changes" badge so admins know whether they're editing a draft.
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [baseline, setBaseline] = useState({
    subject: DEFAULT_SUBJECT,
    preheader: DEFAULT_PREHEADER,
    html: DEFAULT_HTML,
  });

  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const iframeRef = useRef(null);

  // Render preview using a srcDoc-driven iframe so the HTML renders in isolation.
  //
  // We rewrite `https://defera.app/...` image URLs to the current origin so
  // assets in /public render in the preview before they're deployed to
  // production. The HTML that actually gets SENT keeps the absolute URLs so
  // emails work from any inbox.
  const previewSrcDoc = useMemo(() => {
    const preheaderBlock = preheader
      ? `<div style="display:none;max-height:0;overflow:hidden;font-size:1px;line-height:1px;color:transparent;opacity:0">${escapeHtml(preheader)}</div>`
      : '';

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    // Swap absolute defera.app URLs to the dev origin so local /public files load.
    // Skip the rewrite if we're already on defera.app (production preview).
    const rewritten =
      origin && !origin.includes('defera.app')
        ? html.replace(/https:\/\/defera\.app(\/[^"']*?\.(?:png|jpg|jpeg|webp|gif|svg))/gi, `${origin}$1`)
        : html;

    if (/<body[^>]*>/i.test(rewritten)) {
      return rewritten.replace(/<body([^>]*)>/i, `<body$1>${preheaderBlock}`);
    }
    return `${preheaderBlock}${rewritten}`;
  }, [html, preheader]);

  const loadAudiences = async () => {
    setAudiencesLoading(true);
    setAudiencesError(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/admin/marketing/resend/audiences', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load audiences');
      setAudiences(data.audiences || []);
      setDefaultFrom(data.defaultFrom || '');
      // Auto-select first audience for convenience.
      if (!audienceId && data.audiences?.length) {
        setAudienceId(data.audiences[0].id);
      }
    } catch (e) {
      setAudiencesError(e.message);
    } finally {
      setAudiencesLoading(false);
    }
  };

  useEffect(() => {
    loadAudiences();
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callApi = async (path, body, method = 'POST') => {
    const { supabase } = await import('../lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(path, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session?.access_token}`,
      },
      body: body == null ? undefined : JSON.stringify(body),
    });
    // DELETE may return 204 with no body in some setups; tolerate empty.
    const text = await res.text();
    const data = text ? JSON.parse(text) : {};
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  // Load all saved templates. If none exist yet, seed the built-in
  // "What's new at Defera" template so admins always have a starting point.
  const loadTemplates = async ({ preserveSelection = true } = {}) => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/admin/marketing/templates', {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load templates');

      let list = data.templates || [];

      // First-run seed: persist the built-in starter so future visits show it
      // in the picker. Skip seeding if we already have any saved templates.
      if (list.length === 0) {
        try {
          const seeded = await callApi('/api/admin/marketing/templates', {
            name: DEFAULT_TEMPLATE_NAME,
            subject: DEFAULT_SUBJECT,
            preheader: DEFAULT_PREHEADER,
            html: DEFAULT_HTML,
          });
          if (seeded?.template) list = [seeded.template];
        } catch (seedErr) {
          // Non-fatal — admin can still save manually.
          console.warn('Failed to seed default template', seedErr);
        }
      }

      setTemplates(list);

      // Auto-load the first template (or keep current selection) so the
      // composer always reflects the picker rather than ambient defaults.
      const keepId = preserveSelection && selectedTemplateId
        ? list.find((t) => t.id === selectedTemplateId)?.id
        : null;
      const targetId = keepId || list[0]?.id || '';
      if (targetId) {
        const target = list.find((t) => t.id === targetId);
        if (target && targetId !== selectedTemplateId) applyTemplate(target);
        else if (target && targetId === selectedTemplateId) {
          // Refresh baseline so unsaved-changes detection stays accurate.
          setBaseline({ subject: target.subject, preheader: target.preheader, html: target.html });
        }
      }
    } catch (e) {
      setTemplatesError(e.message);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const applyTemplate = (tpl) => {
    setSelectedTemplateId(tpl.id);
    setSubject(tpl.subject || '');
    setPreheader(tpl.preheader || '');
    setHtml(tpl.html || '');
    setBaseline({
      subject: tpl.subject || '',
      preheader: tpl.preheader || '',
      html: tpl.html || '',
    });
    setResult(null);
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) || null;
  const hasUnsavedChanges =
    subject !== baseline.subject ||
    preheader !== baseline.preheader ||
    html !== baseline.html;

  const handleTemplateChange = (id) => {
    if (id === selectedTemplateId) return;
    if (hasUnsavedChanges) {
      const ok = window.confirm(
        'You have unsaved changes that will be discarded. Switch templates anyway?'
      );
      if (!ok) return;
    }
    const tpl = templates.find((t) => t.id === id);
    if (tpl) applyTemplate(tpl);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    setTemplateBusy(true);
    setResult(null);
    try {
      const data = await callApi(
        `/api/admin/marketing/templates/${selectedTemplate.id}`,
        { subject, preheader, html },
        'PUT'
      );
      const updated = data.template;
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setBaseline({
        subject: updated.subject,
        preheader: updated.preheader,
        html: updated.html,
      });
      setResult({ success: true, message: `Saved "${updated.name}"` });
    } catch (e) {
      setResult({ success: false, message: e.message });
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleSaveAs = async () => {
    const name = saveAsName.trim();
    if (!name) return;
    setTemplateBusy(true);
    setResult(null);
    try {
      const data = await callApi('/api/admin/marketing/templates', {
        name,
        subject,
        preheader,
        html,
      });
      const created = data.template;
      setTemplates((prev) => [created, ...prev.filter((t) => t.id !== created.id)]);
      applyTemplate(created);
      setSaveAsOpen(false);
      setSaveAsName('');
      setResult({ success: true, message: `Created "${created.name}"` });
    } catch (e) {
      setResult({ success: false, message: e.message });
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleRename = async () => {
    if (!selectedTemplate) return;
    const name = renameValue.trim();
    if (!name || name === selectedTemplate.name) {
      setRenameOpen(false);
      return;
    }
    setTemplateBusy(true);
    setResult(null);
    try {
      const data = await callApi(
        `/api/admin/marketing/templates/${selectedTemplate.id}`,
        { name },
        'PUT'
      );
      const updated = data.template;
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setRenameOpen(false);
      setResult({ success: true, message: `Renamed to "${updated.name}"` });
    } catch (e) {
      setResult({ success: false, message: e.message });
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    const confirmMsg = `Delete the template "${selectedTemplate.name}"? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setTemplateBusy(true);
    setResult(null);
    try {
      await callApi(`/api/admin/marketing/templates/${selectedTemplate.id}`, null, 'DELETE');
      const remaining = templates.filter((t) => t.id !== selectedTemplate.id);
      setTemplates(remaining);
      if (remaining[0]) {
        applyTemplate(remaining[0]);
      } else {
        setSelectedTemplateId('');
      }
      setResult({ success: true, message: 'Template deleted' });
    } catch (e) {
      setResult({ success: false, message: e.message });
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleTest = async () => {
    if (!subject.trim()) return setResult({ success: false, message: 'Subject is required' });
    if (!html.trim()) return setResult({ success: false, message: 'HTML body is required' });

    setSending(true);
    setResult(null);
    try {
      const data = await callApi('/api/admin/marketing/resend/test', {
        subject,
        preheader,
        html,
      });
      setResult({ success: true, message: `Test sent to ${data.to}` });
    } catch (e) {
      setResult({ success: false, message: e.message });
    } finally {
      setSending(false);
    }
  };

  const handleBroadcast = async () => {
    if (!subject.trim()) return setResult({ success: false, message: 'Subject is required' });
    if (!html.trim()) return setResult({ success: false, message: 'HTML body is required' });
    if (!audienceId) return setResult({ success: false, message: 'Pick a Resend audience' });

    const audience = audiences.find((a) => a.id === audienceId);
    const confirmMsg =
      `Send this email to ALL contacts in the "${audience?.name || audienceId}" audience?\n\n` +
      `Subject: ${subject}\n\nThis cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    setResult(null);
    try {
      const data = await callApi('/api/admin/marketing/resend/broadcast', {
        audienceId,
        subject,
        preheader,
        html,
      });
      setResult({
        success: true,
        message: `Broadcast sent! ID: ${data.broadcastId}. Check Resend dashboard for delivery.`,
      });
    } catch (e) {
      setResult({ success: false, message: e.message });
    } finally {
      setSending(false);
    }
  };

  const previewWidth = viewport === 'mobile' ? 390 : 680;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-[1400px] mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Mail className="w-7 h-7 text-white" />
              <h1 className="text-3xl font-bold tracking-tight">Marketing</h1>
            </div>
            <p className="text-gray-400 text-sm">
              Compose a campaign, preview it live, then blast to all contacts in a Resend audience.
            </p>
            <a href="/destination-content" className="mt-2 inline-flex items-center gap-1 text-sm text-[#6f86ff] hover:text-[#96a7ff]">
              <MapPin className="w-3.5 h-3.5" /> Destination Library — edit the “destination is calling” emails →
            </a>
          </div>
          {defaultFrom && (
            <div className="text-xs text-gray-500">
              From <span className="text-gray-300 font-mono">{defaultFrom}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(320px,440px)_1fr] gap-6">
          {/* Composer */}
          <Card className="bg-zinc-950 border-zinc-800 p-5 space-y-5 h-fit">
            {/* Template picker */}
            <div className="pb-4 border-b border-zinc-800">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-normal text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Template
                  {hasUnsavedChanges && selectedTemplate && (
                    <span className="ml-1 inline-flex items-center gap-1 normal-case tracking-normal text-[10px] font-normal text-amber-300 bg-amber-950/40 border border-amber-900/60 rounded-full px-1.5 py-0.5">
                      Unsaved
                    </span>
                  )}
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      // Pull the latest in-code default into the composer.
                      // Useful after a starter template constant is updated
                      // in source — saved server-side templates won't pick
                      // up code changes on their own, so this lets an admin
                      // overlay the latest defaults and then hit Save to
                      // persist. We pick which constant to load based on
                      // the selected template's name so the "Welcome to
                      // Defera" picker entry gets WELCOME_DEFAULT_*, and
                      // everything else (including the original broadcast)
                      // gets the marketing DEFAULT_*.
                      const isWelcome = (selectedTemplate?.name || '')
                        .toLowerCase()
                        .includes('welcome');
                      if (isWelcome) {
                        setSubject(WELCOME_DEFAULT_SUBJECT);
                        setPreheader(WELCOME_DEFAULT_PREHEADER);
                        setHtml(WELCOME_DEFAULT_HTML);
                      } else {
                        setSubject(DEFAULT_SUBJECT);
                        setPreheader(DEFAULT_PREHEADER);
                        setHtml(DEFAULT_HTML);
                      }
                    }}
                    className="text-xs normal-case tracking-normal text-gray-500 hover:text-white flex items-center gap-1"
                    title="Replace the composer with the latest built-in starter template (then click Save to update the saved template)"
                  >
                    <Download className="w-3 h-3" />
                    Load defaults
                  </button>
                  <button
                    type="button"
                    onClick={() => loadTemplates()}
                    disabled={templatesLoading}
                    className="text-xs normal-case tracking-normal text-gray-500 hover:text-white flex items-center gap-1 disabled:opacity-50"
                    title="Reload templates"
                  >
                    <RefreshCw className={`w-3 h-3 ${templatesLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>
              <Select
                value={selectedTemplateId}
                onValueChange={handleTemplateChange}
                disabled={templatesLoading || !templates.length}
              >
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                  <SelectValue placeholder={
                    templatesLoading
                      ? 'Loading templates...'
                      : templates.length
                        ? 'Select a template'
                        : 'No templates yet'
                  } />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id} className="focus:bg-zinc-800 focus:text-white">
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templatesError && (
                <p className="text-xs text-red-400 mt-1.5 flex items-start gap-1">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  {templatesError}
                </p>
              )}
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveTemplate}
                  disabled={!selectedTemplate || !hasUnsavedChanges || templateBusy}
                  className="bg-zinc-900 hover:bg-zinc-800 text-white border-zinc-700 h-8 text-xs"
                  title={
                    !selectedTemplate
                      ? 'Select a template first'
                      : !hasUnsavedChanges
                        ? 'No changes to save'
                        : 'Save changes to this template'
                  }
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSaveAsName(selectedTemplate ? `${selectedTemplate.name} (copy)` : '');
                    setSaveAsOpen(true);
                  }}
                  disabled={templateBusy}
                  className="bg-zinc-900 hover:bg-zinc-800 text-white border-zinc-700 h-8 text-xs"
                  title="Save the current composer state as a new template"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Save as new
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setRenameValue(selectedTemplate?.name || '');
                    setRenameOpen(true);
                  }}
                  disabled={!selectedTemplate || templateBusy}
                  className="bg-zinc-900 hover:bg-zinc-800 text-white border-zinc-700 h-8 text-xs"
                  title="Rename this template"
                >
                  Rename
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDeleteTemplate}
                  disabled={!selectedTemplate || templateBusy}
                  className="bg-zinc-900 hover:bg-red-950/40 text-red-300 hover:text-red-200 border-zinc-700 hover:border-red-900/60 h-8 text-xs"
                  title="Delete this template"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Delete
                </Button>
              </div>
            </div>

            <div>
              <label className="text-xs font-normal text-gray-400 uppercase tracking-wider mb-1.5 block">
                Subject
              </label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="A better way to fly is here"
                className="bg-zinc-900 border-zinc-800 text-white placeholder:text-gray-600"
              />
            </div>

            <div>
              <label className="text-xs font-normal text-gray-400 uppercase tracking-wider mb-1.5 block">
                Preheader
                <span className="ml-1 normal-case tracking-normal text-gray-600">
                  (inbox preview text)
                </span>
              </label>
              <Input
                value={preheader}
                onChange={(e) => setPreheader(e.target.value)}
                placeholder="Book now, pay over time. No interest, no surprises."
                className="bg-zinc-900 border-zinc-800 text-white placeholder:text-gray-600"
              />
              <p className="text-xs text-gray-600 mt-1">
                Hidden in the email body, shown next to the subject in most inboxes.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-normal text-gray-400 uppercase tracking-wider">
                  HTML body
                </label>
                <button
                  type="button"
                  onClick={() => {
                    if (selectedTemplate) {
                      // Revert composer to the saved version of the current template.
                      setSubject(baseline.subject);
                      setPreheader(baseline.preheader);
                      setHtml(baseline.html);
                    } else {
                      setSubject(DEFAULT_SUBJECT);
                      setPreheader(DEFAULT_PREHEADER);
                      setHtml(DEFAULT_HTML);
                    }
                  }}
                  disabled={selectedTemplate ? !hasUnsavedChanges : false}
                  className="text-xs text-gray-500 hover:text-white flex items-center gap-1 disabled:opacity-50 disabled:hover:text-gray-500"
                  title={
                    selectedTemplate
                      ? 'Revert composer to the saved version of this template'
                      : 'Reset to default starter template'
                  }
                >
                  <Sparkles className="w-3 h-3" />
                  {selectedTemplate ? 'Revert changes' : 'Reset template'}
                </button>
              </div>
              <Textarea
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                placeholder="<html>...</html>"
                spellCheck={false}
                className="bg-zinc-900 border-zinc-800 text-gray-200 font-mono text-xs leading-relaxed min-h-[360px] resize-y"
              />
              <p className="text-xs text-gray-600 mt-1">
                Tip: Resend injects an unsubscribe URL wherever you put
                <code className="mx-1 px-1 py-0.5 rounded bg-zinc-800 text-gray-300">{'{{{RESEND_UNSUBSCRIBE_URL}}}'}</code>
                — required by Resend for broadcasts.
              </p>
            </div>

            {/* Audience picker */}
            <div className="pt-2 border-t border-zinc-800">
              <label className="text-xs font-normal text-gray-400 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                <span>Resend audience</span>
                <button
                  type="button"
                  onClick={loadAudiences}
                  disabled={audiencesLoading}
                  className="normal-case tracking-normal text-gray-500 hover:text-white flex items-center gap-1 disabled:opacity-50"
                  title="Reload audiences from Resend"
                >
                  <RefreshCw className={`w-3 h-3 ${audiencesLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </label>
              <Select value={audienceId} onValueChange={setAudienceId} disabled={audiencesLoading}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                  <SelectValue placeholder={
                    audiencesLoading
                      ? 'Loading audiences...'
                      : audiences.length
                        ? 'Select an audience'
                        : 'No audiences found'
                  } />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                  {audiences.map((a) => (
                    <SelectItem key={a.id} value={a.id} className="focus:bg-zinc-800 focus:text-white">
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {audiencesError && (
                <p className="text-xs text-red-400 mt-1.5 flex items-start gap-1">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  {audiencesError}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 pt-2 border-t border-zinc-800">
              <Button
                onClick={handleTest}
                disabled={sending}
                variant="outline"
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white border-zinc-700"
              >
                <TestTube className="w-4 h-4 mr-2" />
                Send test to me
              </Button>
              <Button
                onClick={handleBroadcast}
                disabled={sending || !audienceId}
                className="w-full bg-white hover:bg-gray-200 text-black font-semibold"
              >
                <Users className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : 'Send to all contacts'}
              </Button>
            </div>

            {result && (
              <div
                className={`flex items-start gap-2 p-3 rounded-lg text-sm ${
                  result.success
                    ? 'bg-green-950/40 border border-green-900/60 text-green-300'
                    : 'bg-red-950/40 border border-red-900/60 text-red-300'
                }`}
              >
                {result.success ? (
                  <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                )}
                <span className="break-words">{result.message}</span>
              </div>
            )}
          </Card>

          {/* Preview */}
          <Card className="bg-zinc-950 border-zinc-800 p-0 overflow-hidden">
            {/* Preview chrome */}
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-zinc-800">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setTab('preview')}
                  className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition ${
                    tab === 'preview'
                      ? 'bg-zinc-800 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Eye className="w-3.5 h-3.5" /> Preview
                </button>
                <button
                  type="button"
                  onClick={() => setTab('source')}
                  className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition ${
                    tab === 'source'
                      ? 'bg-zinc-800 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Code2 className="w-3.5 h-3.5" /> Source
                </button>
              </div>
              <div className="flex items-center gap-1 bg-zinc-900 rounded-md p-0.5">
                <button
                  type="button"
                  onClick={() => setViewport('desktop')}
                  className={`text-xs px-2.5 py-1 rounded flex items-center gap-1.5 transition ${
                    viewport === 'desktop'
                      ? 'bg-zinc-700 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Monitor className="w-3.5 h-3.5" /> Desktop
                </button>
                <button
                  type="button"
                  onClick={() => setViewport('mobile')}
                  className={`text-xs px-2.5 py-1 rounded flex items-center gap-1.5 transition ${
                    viewport === 'mobile'
                      ? 'bg-zinc-700 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Smartphone className="w-3.5 h-3.5" /> Mobile
                </button>
              </div>
            </div>

            {/* Inbox row preview — what they'd see in Gmail */}
            <div className="px-4 py-3 bg-zinc-900/40 border-b border-zinc-800">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Inbox preview</div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-600 to-brand-900 flex-shrink-0 flex items-center justify-center text-xs font-semibold">D</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-white text-sm truncate">
                      {defaultFrom?.replace(/<.*>/, '').trim() || 'Defera'}
                    </span>
                    <span className="text-xs text-gray-500 ml-auto flex-shrink-0">now</span>
                  </div>
                  <div className="text-sm text-gray-200 truncate">{subject || '(no subject)'}</div>
                  <div className="text-xs text-gray-500 truncate">{preheader || '(no preheader)'}</div>
                </div>
              </div>
            </div>

            {/* Preview body */}
            <div className="bg-zinc-900/40 p-4 sm:p-6 min-h-[600px]">
              {tab === 'preview' ? (
                <div className="flex justify-center">
                  <div
                    className="bg-white rounded-lg shadow-2xl overflow-hidden transition-all duration-300"
                    style={{
                      width: '100%',
                      maxWidth: previewWidth,
                    }}
                  >
                    <iframe
                      ref={iframeRef}
                      title="Email preview"
                      srcDoc={previewSrcDoc}
                      sandbox="allow-same-origin"
                      style={{
                        width: '100%',
                        height: viewport === 'mobile' ? 720 : 800,
                        border: 'none',
                        display: 'block',
                        background: '#ffffff',
                      }}
                    />
                  </div>
                </div>
              ) : (
                <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap break-words bg-black/40 rounded-lg p-4 border border-zinc-800 overflow-auto max-h-[700px]">
                  {html}
                </pre>
              )}
            </div>
          </Card>
        </div>

        {/* Footer help */}
        <div className="mt-6 text-[13px] text-gray-400 flex items-start gap-2">
          <Send className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Test sends use the Resend <code className="text-gray-300">/emails</code> endpoint to your admin email.
            "Send to all contacts" creates a Resend Broadcast for the selected audience and dispatches it immediately.
            Status lives in the <a href="https://resend.com/broadcasts" target="_blank" rel="noreferrer" className="text-brand-300 hover:text-white underline">Resend dashboard</a>.
          </span>
        </div>
      </div>

      {/* Save as new template dialog */}
      <Dialog open={saveAsOpen} onOpenChange={(open) => !templateBusy && setSaveAsOpen(open)}>
        <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white">
          <DialogHeader>
            <DialogTitle>Save as new template</DialogTitle>
            <DialogDescription className="text-gray-400">
              Give this template a memorable name. You&apos;ll be able to load it again from the picker.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-normal text-gray-400 uppercase tracking-wider mb-1.5 block">
              Template name
            </label>
            <Input
              value={saveAsName}
              onChange={(e) => setSaveAsName(e.target.value)}
              placeholder="e.g. Black Friday 2026"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && saveAsName.trim() && !templateBusy) handleSaveAs();
              }}
              className="bg-zinc-900 border-zinc-800 text-white placeholder:text-gray-600"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setSaveAsOpen(false)}
              disabled={templateBusy}
              className="bg-zinc-900 hover:bg-zinc-800 text-white border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveAs}
              disabled={!saveAsName.trim() || templateBusy}
              className="bg-white hover:bg-gray-200 text-black font-semibold"
            >
              {templateBusy ? 'Saving...' : 'Save template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename template dialog */}
      <Dialog open={renameOpen} onOpenChange={(open) => !templateBusy && setRenameOpen(open)}>
        <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white">
          <DialogHeader>
            <DialogTitle>Rename template</DialogTitle>
            <DialogDescription className="text-gray-400">
              Pick a new name for &quot;{selectedTemplate?.name}&quot;.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-normal text-gray-400 uppercase tracking-wider mb-1.5 block">
              Template name
            </label>
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && renameValue.trim() && !templateBusy) handleRename();
              }}
              className="bg-zinc-900 border-zinc-800 text-white placeholder:text-gray-600"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setRenameOpen(false)}
              disabled={templateBusy}
              className="bg-zinc-900 hover:bg-zinc-800 text-white border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleRename}
              disabled={!renameValue.trim() || templateBusy}
              className="bg-white hover:bg-gray-200 text-black font-semibold"
            >
              {templateBusy ? 'Renaming...' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
